import React, { useMemo } from 'react'
import { Autocomplete, Box, TextField } from '@mui/material'
import { Table } from 'react-bootstrap'

import DropdownActionButtonMui from '../../../../components/action_button/DropdownActionButtonMui'
import { STORE_RELATIONS_FALLBACK_TEXT } from '../../constants/storeConstants'
import AssignmentBrandSelect from './AssignmentBrandSelect'
import {
  AUTOCOMPLETE_POPPER_SLOT_PROPS,
  STORE_RELATION_FORM_ASSIGNMENT_EMPTY_MESSAGES,
  STORE_RELATION_FORM_ASSIGNMENT_TABLE_COLUMNS,
  STORE_RELATION_FORM_TEXT
} from '../constants/storeRelationFormConstants'

const StoreRelationBrandAssignmentsSection = ({
  isEditMode = false,
  clientDbRef = '',
  assignmentRows = [],
  assignmentUserSearch = '',
  assignmentSalesmanOptions = [],
  assignmentSalesmenLoading = false,
  selectedBrandDbRefsByRow = {},
  initialAssignedBrandDbRefs = [],
  initialBrandOptionsByDbRef = {},
  unavailableBrandDbRefs = [],
  onAssignmentSalesmanSelect,
  onAssignmentUserSearchChange,
  onAssignmentBrandChange,
  onOpenReassignModal,
  onRemoveAssignmentRow
}) => {
  const filteredAssignmentSalesmanOptions = useMemo(() => {
    return assignmentSalesmanOptions.filter(
      (option) =>
        !assignmentRows.some((row) => row.id === option.id || row.salesmanDbRef === option.db_ref)
    )
  }, [assignmentRows, assignmentSalesmanOptions])

  const currentBrandDbRefs = useMemo(() => {
    return [
      ...new Set([...initialAssignedBrandDbRefs, ...Object.values(selectedBrandDbRefsByRow).flat()])
    ]
  }, [initialAssignedBrandDbRefs, selectedBrandDbRefsByRow])

  return (
    <div className="store-relations-form__section">
      <h4 className="store-relations-form__section-title">
        {STORE_RELATION_FORM_TEXT.BRAND_ASSIGNMENT_SECTION_TITLE}
      </h4>
      <p className="store-relations-form__section-text">
        {STORE_RELATION_FORM_TEXT.BRAND_ASSIGNMENT_SECTION_TEXT}
      </p>

      <hr />

      <Autocomplete
        className="store-relations-form__autocomplete"
        fullWidth
        filterSelectedOptions
        slotProps={{ popper: AUTOCOMPLETE_POPPER_SLOT_PROPS }}
        options={filteredAssignmentSalesmanOptions}
        loading={assignmentSalesmenLoading}
        value={null}
        inputValue={assignmentUserSearch}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        getOptionLabel={(option) => option?.label || ''}
        onChange={(_, nextOption) => {
          onAssignmentSalesmanSelect(nextOption)
        }}
        onInputChange={(_, nextInputValue, reason) => {
          if (reason === 'reset') return

          onAssignmentUserSearchChange(nextInputValue)
        }}
        noOptionsText={STORE_RELATION_FORM_TEXT.NO_OPTIONS}
        loadingText={STORE_RELATION_FORM_TEXT.LOADING_USERS}
        renderInput={(params) => (
          <TextField {...params} size="small" label={STORE_RELATION_FORM_TEXT.USER_SEARCH_LABEL} />
        )}
      />

      <Box mt={2}>
        <Table hover responsive className="oc-fixed-table">
          <thead className="table-header-gray-poc">
            <tr>
              {STORE_RELATION_FORM_ASSIGNMENT_TABLE_COLUMNS.map((column) => (
                <th key={column.key} className={column.className}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {assignmentRows.length === 0 ? (
              <tr>
                <td colSpan={STORE_RELATION_FORM_ASSIGNMENT_TABLE_COLUMNS.length}>
                  {isEditMode
                    ? STORE_RELATION_FORM_ASSIGNMENT_EMPTY_MESSAGES.EDIT
                    : STORE_RELATION_FORM_ASSIGNMENT_EMPTY_MESSAGES.CREATE}
                </td>
              </tr>
            ) : (
              assignmentRows.map((row) => {
                const selectedBrandDbRefsInOtherRows = Object.entries(selectedBrandDbRefsByRow)
                  .filter(([currentRowId]) => currentRowId !== row.id)
                  .flatMap(([, brandDbRefs]) => brandDbRefs)
                const locallyAvailableBrandOptions = Object.entries(initialBrandOptionsByDbRef)
                  .filter(
                    ([brandDbRef]) =>
                      !Object.entries(selectedBrandDbRefsByRow)
                        .filter(([currentRowId]) => currentRowId !== row.id)
                        .some(([, brandDbRefs]) => brandDbRefs.includes(brandDbRef))
                  )
                  .map(([, brandOption]) => brandOption)

                return (
                  <tr
                    key={row.id}
                    className={row.allBrandsInError ? 'store-relations-user-row--error-soft' : ''}
                  >
                    <td className="oc-td-text">{row.userLabel || STORE_RELATIONS_FALLBACK_TEXT}</td>
                    <td className="oc-td-text">
                      <AssignmentBrandSelect
                        row={row}
                        clientDbRef={clientDbRef}
                        currentBrandDbRefs={currentBrandDbRefs}
                        selectedBrandDbRefsInOtherRows={selectedBrandDbRefsInOtherRows}
                        locallyAvailableBrandOptions={locallyAvailableBrandOptions}
                        unavailableBrandDbRefs={unavailableBrandDbRefs}
                        onChange={onAssignmentBrandChange}
                      />
                    </td>

                    <td className="oc-action-cell ">
                      <DropdownActionButtonMui
                        list={[
                          {
                            text: STORE_RELATION_FORM_TEXT.REASSIGN_USER_ACTION,
                            handler: () => onOpenReassignModal(row)
                          },
                          {
                            text: STORE_RELATION_FORM_TEXT.DELETE_ACTION,
                            handler: () => onRemoveAssignmentRow(row.id)
                          }
                        ]}
                      />
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </Box>
    </div>
  )
}

export default StoreRelationBrandAssignmentsSection
